import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, fmtGBP, fmtDate, formatErr } from "@/lib/api";
import { useAuth, ROLE_LABEL, canJobAction, can } from "@/lib/auth";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import {
  ArrowLeft, UsersThree, LinkSimple, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { JobPhotosCard } from "@/components/job/JobPhotosCard";
import { JobStagesCard } from "@/components/job/JobStagesCard";
import { JobAssignmentCard } from "@/components/job/JobAssignmentCard";
import { JobTimeTrackingCard } from "@/components/job/JobTimeTrackingCard";
import { JobNotesCard } from "@/components/job/JobNotesCard";
import { Action } from "@/components/job/JobAction";
import { JobActionsBar } from "@/components/job/JobActionsBar";
import { JobFeedbackCard } from "@/components/job/JobFeedbackCard";
import { JobWorkflowCard } from "@/components/job/JobWorkflowCard";

const APPROVERS = ["admin", "general_manager", "district_manager", "area_manager", "maintenance_manager", "store_manager"];

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: job, error, refetch } = useQuery({ queryKey: ["job", id], queryFn: () => http.get(`/jobs/${id}`).then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => http.get("/users").then(r => r.data) });
  const { data: limits = [] } = useQuery({ queryKey: ["role-limits"], queryFn: () => http.get("/role-limits").then(r => r.data) });
  const { data: equipment = [] } = useQuery({ queryKey: ["equipment"], queryFn: () => http.get("/equipment").then(r => r.data) });

  const [assignTo, setAssignTo] = useState("");
  const [approverId, setApproverId] = useState("");
  const [multiAssign, setMultiAssign] = useState([]);
  const [showMulti, setShowMulti] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [newStageAssignee, setNewStageAssignee] = useState("");
  const [comments, setComments] = useState("");
  const [actualCost, setActualCost] = useState(0);
  const [rating, setRating] = useState(5);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState("during");
  const [copied, setCopied] = useState(false);

  const copyJobLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Job link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn("[clipboard] copy failed", e);
      toast.error("Couldn't copy — please copy from the address bar.");
    }
  };

  if (error) return <div className="p-8 text-rose-600">Failed to load: {formatErr(error.response?.data?.detail)}</div>;
  if (!job) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const techs = users.filter(u => ["maintenance_staff", "contractor"].includes(u.role));
  const store = stores.find(s => s.id === job.store_id);
  const assignee = users.find(u => u.id === job.assigned_to_id);
  const myLimit = limits.find(l => l.role === user.role)?.max_approval_amount_gbp;

  const isApprover = canJobAction(user, "approve");
  const canEditJobs = can(user, "jobs", "edit");
  const isAssignee = job.assigned_to_id === user.id;
  const canAssignDirect = canJobAction(user, "assign");
  const isCreator = job.created_by_id === user.id;
  const canSendForApproval = (canAssignDirect || isApprover || isCreator) && ["draft", "submitted", "approved", "rejected"].includes(job.status);
  const approverUsers = users.filter(u => APPROVERS.includes(u.role));
  const myPoolEntry = (job.assignee_pool || []).find(p => p.user_id === user.id);
  const canAccept = job.status === "assigned" && !job.assigned_to_id && myPoolEntry && (myPoolEntry.status === "pending" || myPoolEntry.status === "on_hold");
  const stages = job.stages || [];

  const act = async (path, body = {}) => {
    setBusy(true); setErr("");
    try {
      await http.post(`/jobs/${id}/${path}`, { ...body, expected_updated_at: job.updated_at });
      await qc.invalidateQueries({ queryKey: ["job", id] });
      await qc.invalidateQueries({ queryKey: ["jobs"] });
      await qc.invalidateQueries({ queryKey: ["summary"] });
      await refetch();
      setComments(""); setActualCost(0);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const addStage = async () => {
    setBusy(true); setErr("");
    try {
      await http.post(`/jobs/${id}/stages`, { name: newStageName.trim(), assigned_to_id: newStageAssignee || null, order: (job.stages || []).length });
      setNewStageName(""); setNewStageAssignee("");
      await refetch();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const patchStage = async (sid, patch) => {
    setBusy(true); setErr("");
    try {
      await http.patch(`/jobs/${id}/stages/${sid}`, patch);
      await refetch();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteStage = async (sid) => {
    if (!window.confirm("Delete this stage?")) return;
    setBusy(true); setErr("");
    try {
      await http.delete(`/jobs/${id}/stages/${sid}`);
      await refetch();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitFeedback = async () => {
    setBusy(true); setErr("");
    try {
      await http.post(`/jobs/${id}/feedback`, { rating: Number(rating), comments });
      await refetch();
      setComments("");
    } catch (e) { setErr(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setErr("");
    try {
      const fileIds = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const r = await http.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        fileIds.push(r.data.id);
      }
      await http.post(`/jobs/${id}/photos`, { file_ids: fileIds, phase: uploadPhase });
      await refetch();
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const onRemovePhoto = async (fid) => {
    try {
      await http.delete(`/jobs/${id}/photos/${fid}`);
      await refetch();
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <button onClick={() => navigate(-1)} data-testid="job-back" className="text-xs text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="overline">Job</span>
            <span className="font-mono-data text-[12px] text-slate-500">{job.job_number}</span>
            <StatusBadge status={job.status} />
            <PriorityBadge priority={job.priority} />
            {job.sla_breached && <span className="text-[11px] font-heading uppercase tracking-wider font-semibold border rounded-sm px-2 py-0.5 bg-rose-50 text-rose-700 border-rose-300">SLA Breached</span>}
            <button
              onClick={copyJobLink}
              data-testid="job-copy-link"
              title="Copy job link"
              className={`inline-flex items-center gap-1 text-[10px] font-heading uppercase tracking-wider px-1.5 py-0.5 border rounded-sm transition-colors ${
                copied ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-slate-200 text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7] hover:bg-slate-50"
              }`}
            >
              {copied ? <><Check size={9} weight="bold" /> Copied</> : <><LinkSimple size={9} weight="bold" /> Copy link</>}
            </button>
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight" data-testid="job-title">{job.title}</h1>
          <div className="text-sm text-slate-600 mt-1">{store?.name || "—"} · Due {fmtDate(job.due_date)}</div>
          {job.equipment_id && (() => {
            const eq = equipment.find(e => e.id === job.equipment_id);
            return eq ? (
              <div className="mt-2 inline-flex items-center gap-2 text-xs px-2 py-1 border border-slate-200 rounded-sm bg-slate-50" data-testid="job-linked-equipment">
                <span className="overline">Equipment</span>
                <span className="font-medium">{eq.name}</span>
                {eq.model && <span className="font-mono-data text-slate-500">{eq.model}</span>}
                {eq.location && <span className="text-slate-500">· {eq.location}</span>}
              </div>
            ) : null;
          })()}
        </div>

        <JobActionsBar
          job={job}
          isApprover={isApprover}
          canAssignDirect={canAssignDirect}
          isAssignee={isAssignee}
          canSendForApproval={canSendForApproval}
          canAccept={canAccept}
          assignTo={assignTo}
          approverId={approverId}
          setApproverId={setApproverId}
          approverUsers={approverUsers}
          showMulti={showMulti}
          setShowMulti={setShowMulti}
          comments={comments}
          actualCost={actualCost}
          busy={busy}
          act={act}
          setErr={setErr}
        />
      </div>

      {err && <div data-testid="job-error" className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-2 mb-4">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="rmms-card p-5">
            <div className="overline mb-2">Description</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{job.description || "—"}</p>
          </div>

          <div className="rmms-card grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-200">
            {[
              ["Estimated", fmtGBP(job.estimated_cost)],
              ["Actual", fmtGBP(job.actual_cost)],
              ["SLA", `${job.sla_hours}h`],
              ["Approval", job.approval_status || (job.approval_required ? "pending" : "not_required")],
            ].map(([k, v]) => (
              <div key={k} className="p-4">
                <div className="overline">{k}</div>
                <div className="font-mono-data text-lg mt-1 tabular-nums">{v}</div>
              </div>
            ))}
          </div>

          <JobTimeTrackingCard job={job} />

          <JobNotesCard jobId={id} canShare={canEditJobs} currentUserId={user.id} />

          {(job.return_log || []).length > 0 && (
            <div className="rmms-card p-5" data-testid="job-return-log">
              <div className="overline mb-2">Returns</div>
              <ul className="space-y-1.5 text-[12px]">
                {(job.return_log || []).map((r, i) => (
                  <li key={r.at || `return-${i}`} className="flex items-start gap-2">
                    <span className="font-mono-data text-slate-500 mt-0.5">{i + 1}.</span>
                    <div className="flex-1">
                      <div className="font-medium">{r.reason}</div>
                      <div className="text-slate-500 font-mono-data text-[10px]">{fmtDate(r.at)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <JobPhotosCard
            job={job}
            uploadPhase={uploadPhase}
            setUploadPhase={setUploadPhase}
            uploading={uploading}
            onUpload={onUpload}
            onRemovePhoto={onRemovePhoto}
            canRemove={isAssignee || isApprover}
          />

          {((["pending_approval", "submitted", "approved"].includes(job.status) && (canAssignDirect || isApprover)) || (["assigned", "in_progress"].includes(job.status) && canAssignDirect)) && (
            <div className="rmms-card p-5">
              <div className="overline mb-3">{["assigned", "in_progress"].includes(job.status) ? "Reassign technician" : "Approve & Assign"}</div>
              {!showMulti ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="overline mb-1.5 block">Technician / Contractor</label>
                    <select value={assignTo} onChange={e => setAssignTo(e.target.value)} data-testid="job-assignee-select" className="rmms-input">
                      <option value="">Select…</option>
                      {techs.map(t => <option key={t.id} value={t.id}>{t.name} ({ROLE_LABEL[t.role]})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline mb-1.5 block">Comments (optional)</label>
                    <input value={comments} onChange={e => setComments(e.target.value)} data-testid="job-comments-input" className="rmms-input" placeholder="e.g. Use approved vendor list" />
                  </div>
                </div>
              ) : (
                <div data-testid="job-multi-assign-panel">
                  <label className="overline mb-1.5 block">Offer to multiple technicians — first to accept gets the job</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {techs.map(t => (
                      <label key={t.id} className={`flex items-center gap-2 p-2 border rounded-sm cursor-pointer transition-colors text-sm ${
                        multiAssign.includes(t.id) ? "border-[#002FA7] bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                      }`}>
                        <input
                          type="checkbox"
                          checked={multiAssign.includes(t.id)}
                          onChange={() => setMultiAssign(prev => prev.includes(t.id) ? prev.filter(x => x !== t.id) : [...prev, t.id])}
                          data-testid={`job-multi-tech-${t.email}`}
                        />
                        <div>
                          <div className="font-medium">{t.name}</div>
                          <div className="text-[10px] text-slate-500">{ROLE_LABEL[t.role]}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Action testid="job-multi-assign-submit" icon={UsersThree} label={`Offer to ${multiAssign.length || 0} ${multiAssign.length === 1 ? "tech" : "techs"}`}
                      onClick={() => multiAssign.length ? act("assignment-pool", { user_ids: multiAssign }) : setErr("Pick at least one technician.")}
                      disabled={busy || !multiAssign.length} />
                  </div>
                </div>
              )}
              {myLimit !== undefined && myLimit !== -1 && (
                <div className="mt-3 text-xs text-slate-500">
                  Your approval limit: <span className="font-mono-data">{fmtGBP(myLimit)}</span>
                  {job.estimated_cost > myLimit && <span className="text-rose-600 ml-2">Job exceeds your limit.</span>}
                </div>
              )}
            </div>
          )}

          {job.status === "in_progress" && isAssignee && (
            <div className="rmms-card p-5">
              <div className="overline mb-3">Completion</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="overline mb-1.5 block">Actual cost (£)</label>
                  <input type="number" step="0.01" value={actualCost} onChange={e => setActualCost(e.target.value)} data-testid="job-actualcost-input" className="rmms-input font-mono-data" />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Notes</label>
                  <input value={comments} onChange={e => setComments(e.target.value)} data-testid="job-completion-notes-input" className="rmms-input" />
                </div>
              </div>
            </div>
          )}

          {(job.status === "completed" || job.status === "closed") && (
            <JobFeedbackCard
              job={job}
              rating={rating}
              setRating={setRating}
              comments={comments}
              setComments={setComments}
              busy={busy}
              onSubmit={submitFeedback}
            />
          )}
        </div>

        <div className="space-y-4">
          <JobAssignmentCard job={job} store={store} assignee={assignee} users={users} currentUserId={user.id} />

          <JobStagesCard
            stages={stages}
            users={users}
            techs={techs}
            currentUserId={user.id}
            canAssignDirect={canAssignDirect}
            busy={busy}
            newStageName={newStageName}
            setNewStageName={setNewStageName}
            newStageAssignee={newStageAssignee}
            setNewStageAssignee={setNewStageAssignee}
            onAddStage={addStage}
            onPatchStage={patchStage}
            onDeleteStage={deleteStage}
          />

          <JobWorkflowCard audit={job.audit || []} />
        </div>
      </div>
    </div>
  );
}
